/**
 * Clear / Adjust Debt: forgive part or all of what a rider owes.
 *
 * Shows the rider, what they owe week by week, and every clearance made
 * before. The owner picks the weeks (all by default), an amount or the whole
 * balance, and a reason; a confirmation step states the balance before and
 * after. It is a write-off — never recorded as money received, never revenue,
 * and the weeks keep what they charged.
 */

import { useMemo, useState, type ReactNode } from 'react';
import type { RiderDirectoryRow } from '@cenox/shared';
import { Button, ErrorState, Input, Modal, TableSkeleton, Textarea } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { money } from '../../lib/format';
import { useClearRiderDebt, useRiderDebt } from './riderData';

export function ClearDebtDialog({ rider, onClose }: { rider: RiderDirectoryRow; onClose: () => void }): ReactNode {
  const debt = useRiderDebt(rider.id);
  const clear = useClearRiderDebt();
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [all, setAll] = useState(true);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<{ before: string; after: string; amount: string } | null>(null);
  const [key, setKey] = useState(() => `clear-${crypto.randomUUID()}`);
  const fields = clear.error instanceof ApiError ? clear.error.fields : undefined;

  const d = debt.data;
  const currency = d?.currencyCode ?? 'NGN';
  const chosen = useMemo(
    () => (d ? d.weeks.filter((w) => picked === null || picked.has(w.remittanceId)) : []),
    [d, picked],
  );
  const chosenTotal = chosen.reduce((s, w) => s + Number(w.remaining), 0);
  const clearing = all ? chosenTotal : Number(amount || 0);
  const amountValid = all || (/^\d+(\.\d{1,2})?$/.test(amount) && clearing > 0 && clearing <= chosenTotal + 0.0001);
  const outstanding = Number(d?.outstanding ?? 0);

  const toggle = (id: string) => {
    const next = new Set(picked ?? d!.weeks.map((w) => w.remittanceId));
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  return (
    <Modal open onClose={onClose} title={`Clear / adjust debt — ${rider.canonicalName}`} size="lg">
      {debt.isError ? (
        <ErrorState message={debt.error.message} onRetry={() => void debt.refetch()} />
      ) : !d ? (
        <TableSkeleton rows={3} columns={3} />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Rider</p>
              <p className="font-medium text-slate-900">{d.riderName}</p>
              <p className="text-xs text-slate-500">ID {d.externalId ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Outstanding remittance</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900" aria-label="Outstanding remittance">
                {money(d.outstanding, currency)}
              </p>
            </div>
          </div>

          {done ? (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
              Cleared {money(done.amount, currency)}. Outstanding {money(done.before, currency)} → {money(done.after, currency)}.
              Nothing was recorded as received.
            </p>
          ) : null}

          {d.weeks.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing is outstanding.</p>
          ) : confirming ? (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">
                Clear {money(clearing.toFixed(2), currency)} of {d.riderName}’s remittance?
              </p>
              <p>
                Outstanding goes from {money(d.outstanding, currency)} to{' '}
                <strong>{money((outstanding - clearing).toFixed(2), currency)}</strong>. This is a write-off: it is not
                recorded as money received, is never revenue, and the weeks keep what they charged. Payments already
                recorded are not touched.
              </p>
              <p>Reason: {reason}</p>
              {clear.error && !fields ? <ErrorState message={clear.error.message} /> : null}
              {fields?.amount?.[0] ? <p className="text-red-700">{clear.error!.message}</p> : null}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={() => setConfirming(false)}>
                  Back
                </Button>
                <Button
                  type="button"
                  size="sm"
                  loading={clear.isPending}
                  onClick={() =>
                    clear.mutate(
                      {
                        riderId: rider.id,
                        input: {
                          ...(picked !== null ? { remittanceIds: chosen.map((w) => w.remittanceId) } : {}),
                          ...(all ? { clearAll: true } : { amount }),
                          reason,
                          ...(note.trim() ? { note: note.trim() } : {}),
                          idempotencyKey: key,
                        },
                      },
                      {
                        onSuccess: (r) => {
                          setDone({ before: r.data.outstandingBefore, after: r.data.outstandingAfter, amount: r.data.amount });
                          setConfirming(false);
                          setAmount('');
                          setReason('');
                          setNote('');
                          setPicked(null);
                          setAll(true);
                          setKey(`clear-${crypto.randomUUID()}`);
                        },
                      },
                    )
                  }
                >
                  Confirm and save
                </Button>
              </div>
            </div>
          ) : (
            <form
              aria-label="Clear debt"
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                setConfirming(true);
              }}
            >
              <fieldset>
                <legend className="mb-1.5 text-sm font-medium text-slate-700">Weeks</legend>
                <ul className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
                  {d.weeks.map((w) => (
                    <li key={w.remittanceId} className="flex items-center gap-2 px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-4"
                        aria-label={`Clear from ${w.label}`}
                        checked={picked === null || picked.has(w.remittanceId)}
                        onChange={() => toggle(w.remittanceId)}
                      />
                      <span className="flex-1">{w.label}</span>
                      <span className="text-xs text-slate-500">
                        due {money(w.amountDue, currency)}
                        {w.paid !== '0.00' ? ` · paid ${money(w.paid, currency)}` : ''}
                        {w.cleared !== '0.00' ? ` · cleared ${money(w.cleared, currency)}` : ''}
                      </span>
                      <span className="w-28 text-right font-medium tabular-nums">{money(w.remaining, currency)}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-slate-500">
                  Selected: {money(chosenTotal.toFixed(2), currency)}. An amount comes off the oldest selected week first.
                </p>
              </fieldset>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" className="size-4" checked={all} onChange={(e) => setAll(e.target.checked)} />
                Clear the entire balance of the selected weeks
              </label>
              {!all ? (
                <Input
                  label="Amount to clear"
                  inputMode="decimal"
                  required
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  error={amount && !amountValid ? `Up to ${money(chosenTotal.toFixed(2), currency)}` : fields?.amount?.[0]}
                />
              ) : null}
              <Input
                label="Reason for clearance"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Agreed settlement; bike off the road for repairs"
                error={fields?.reason?.[0]}
              />
              <Textarea label="Notes (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Close
                </Button>
                <Button type="submit" disabled={chosen.length === 0 || !amountValid || reason.trim().length < 3}>
                  Review clearance
                </Button>
              </div>
            </form>
          )}

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Cleared before</p>
            {d.clearances.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing cleared before.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" aria-label="Clearances">
                {d.clearances.map((c) => (
                  <li key={c.id} className="px-3 py-2 text-sm">
                    <p className="font-medium text-slate-900">
                      {money(c.amount, currency)} cleared · {money(c.outstandingBefore, currency)} →{' '}
                      {money(c.outstandingAfter, currency)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(c.createdAt).toLocaleString()} · {c.recordedBy ?? 'Unknown'} · {c.reason}
                      {c.note ? ` · ${c.note}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
